import React, { useMemo } from 'react';
import { Card } from 'react-bootstrap';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import './ContractsClosedTable.css';

function ContractsClosedTable({ buildingsData }) {
  const chartData = useMemo(() => {
    if (!buildingsData || buildingsData.length === 0) {
      return [];
    }

    // Filter out buildings without creation dates
    const validBuildings = buildingsData.filter(building => 
      building.createdAt
    );

    if (validBuildings.length === 0) {
      return [];
    }

    // Group by month and count contracts
    const monthlyData = {};

    validBuildings.forEach(building => {
      const date = new Date(building.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthKey,
          contractsClosed: 0,
          totalValue: 0
        };
      }
      
      monthlyData[monthKey].contractsClosed += 1;
      monthlyData[monthKey].totalValue += building.contractValue || 0;
    });

    // Fill in all months since the first contract with zero values
    if (Object.keys(monthlyData).length > 0) {
      const sortedKeys = Object.keys(monthlyData).sort();
      const firstKey = sortedKeys[0];
      
      // Parse first date
      const [firstYear, firstMonth] = firstKey.split('-').map(Number);
      
      // Get current date
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1; // getMonth() returns 0-11, so add 1
      
      // Calculate one month before first contract
      let startYear = firstYear;
      let startMonth = firstMonth - 1;
      if (startMonth < 1) {
        startMonth = 12;
        startYear = startYear - 1;
      }
      
      // Generate all months from one month before first contract until now
      let year = startYear;
      let month = startMonth;
      
      while (year < currentYear || (year === currentYear && month <= currentMonth)) {
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            month: monthKey,
            contractsClosed: 0,
            totalValue: 0
          };
        }
        
        // Move to next month
        month++;
        if (month > 12) {
          month = 1;
          year++;
        }
      }
    }

    // Convert to array and sort by month (oldest first for chart)
    return Object.values(monthlyData)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(item => {
        // Parse the month key (e.g., "2024-11") to get year and month
        const [year, month] = item.month.split('-');
        const monthIndex = parseInt(month) - 1; // Convert to 0-based index
        
        // Create month label using the original date parsing
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthLabel = `${monthNames[monthIndex]} ${year}`;
        
        return {
          ...item,
          month: monthLabel,
          totalValueFormatted: `$${item.totalValue.toLocaleString()}`
        };
      });
  }, [buildingsData]);

  // Calculate total contracts (sum of all contracts arrays in buildingsData)
  const totalContracts = useMemo(() => {
    if (!buildingsData || buildingsData.length === 0) return 0;
    return buildingsData.reduce((sum, b) => sum + (Array.isArray(b.contracts) ? b.contracts.length : 0), 0);
  }, [buildingsData]);

  // Calculate days since most recent contract
  const daysSinceMostRecentContract = useMemo(() => {
    if (!buildingsData || buildingsData.length === 0) return '-';
    let mostRecent = null;
    buildingsData.forEach(b => {
      if (Array.isArray(b.contracts) && b.contracts.length > 0 && b.createdAt) {
        const d = new Date(b.createdAt);
        if (!mostRecent || d > mostRecent) mostRecent = d;
      }
    });
    if (!mostRecent) return '-';
    const now = new Date();
    const diff = Math.floor((now - mostRecent) / (1000 * 60 * 60 * 24));
    return diff;
  }, [buildingsData]);

  if (chartData.length === 0) {
    return (
      <Card className="contracts-table-card">
        <Card.Body>
          <h5 className="table-title">Contracts Closed by Month</h5>
          <div className="no-data-message">
            <p>No contract data available to display.</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="tooltip-value">
            <span style={{ color: '#007bff' }}>Contracts Closed:</span> {payload[0].payload.contractsClosed}
          </p>
          <p className="tooltip-value">
            <span style={{ color: '#28a745' }}>Total Value:</span> {payload[0].payload.totalValueFormatted}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="contracts-table-card">
      <Card.Body>
        <h5 className="table-title">Contracts Closed by Month</h5>
        <div className="table-summary">
          <div className="summary-item">
            <span className="summary-label">Total Contracts:</span>
            <span className="summary-value" style={{ color: '#111' }}>
              {totalContracts}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Avg per Month:</span>
            <span className="summary-value" style={{ color: '#111' }}>
              {Math.round(totalContracts / chartData.length) || 0}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Days Since Last Contract:</span>
            <span className="summary-value" style={{ color: '#111' }}>
              {daysSinceMostRecentContract}
            </span>
          </div>
        </div>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={300} className="no-focus-outline">
            <LineChart data={chartData}>
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="contractsClosed"
                stroke="#007bff"
                strokeWidth={3}
                dot={{ fill: '#007bff', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#007bff', strokeWidth: 2, fill: '#fff', style: { outline: 'none' } }}
                name="Contracts Closed"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card.Body>
    </Card>
  );
}

export default ContractsClosedTable; 